import React, { useCallback, useEffect, useState, useMemo } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import { Tab, Tabs } from '@app/react-common/components/tabs/Tabs';
import { useAppContext } from '@app/react-common/AppContext';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { AbTest } from '../../models/AbTest';
import { AbTestWidgetType, AbTestTypeCategory, AbTestWidgetTypeToTypeCategory } from '../../models/AbTestCreationType';
import { ThemesAllocationSection } from './components/ThemesAllocationSection/ThemesAllocationSection';
import { getIsThemeTest } from '../../utils';
import { AbTestFormData, getInitialFormData } from './shared/abTestModalUtils';
import { AbTestModalHeader } from './shared/AbTestModalHeader';
import { AbTestModalDetailsTab } from './shared/AbTestModalDetailsTab';
import { AbTestModalTrafficAllocationTab } from './shared/AbTestModalTrafficAllocationTab';
import { AbTestModalPreviewTab } from './shared/AbTestModalPreviewTab';
import { useAbTestModalCommon } from './shared/useAbTestModalCommon';
import useThemes from './hooks/useThemes';
import useProductPages from './hooks/useProductPages';
import useSmartCarts from './hooks/useSmartCarts';
import useThemeVariants from './hooks/useThemeVariants';
import ThemeScriptTab from './components/ThemeScriptTab/ThemeScriptTab';
import { PdpPreviewTab } from './components/PdpPreviewTab/PdpPreviewTab';

interface AbTestModalThemesProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  saveTest?: (formData: AbTest) => Promise<number | undefined>;
  existingAbTest?: AbTest;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  runningAbTestCheckoutIds: number[];
  testTypeCategory?: AbTestTypeCategory;
  hasThemePermission?: boolean;
  onTestTypeChange?: (testType: AbTestTypeCategory) => void;
}

export const AbTestModalThemes: React.FC<AbTestModalThemesProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  saveTest,
  existingAbTest,
  mode,
  runningAbTestCheckoutIds,
  testTypeCategory,
  hasThemePermission,
  onTestTypeChange,
}) => {
  const { shop$, showToast } = useAppContext();
  const [activeTab, setActiveTab] = useState('Details');
  const [shop, setShop] = useState<Shop>(null);
  const [trafficAllocationValid, setTrafficAllocationValid] = useState(false);
  const [statisticalSignificanceError, setStatisticalSignificanceError] = useState<string | undefined>(undefined);
  const [fullFormValid, setFullFormValid] = useState(false);
  const [showInvalidStep, setShowInvalidStep] = useState(0);
  const [formData, setFormData] = useState<AbTestFormData>(getInitialFormData(existingAbTest));

  // Reset validation state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowInvalidStep(0);
    }
  }, [isOpen]);

  // Determine widget type from existing test, testTypeCategory prop, or default to CROSS_SITE
  const widgetType = useMemo(() => {
    // For create mode, use testTypeCategory if provided
    if (mode === 'create' && testTypeCategory) {
      if (testTypeCategory === AbTestTypeCategory.CROSS_SITE) {
        return AbTestWidgetType.CROSS_SITE;
      }
      if (testTypeCategory === AbTestTypeCategory.PRODUCT_PAGE) {
        return AbTestWidgetType.PRODUCT_PAGE;
      }
      if (testTypeCategory === AbTestTypeCategory.SMART_CART) {
        return AbTestWidgetType.SMART_CART;
      }
    }

    // For edit/view/duplicate, use existing test data
    if (existingAbTest?.abTestVariants?.[0]?.abTestWidgets?.[0]?.type) {
      const type = existingAbTest.abTestVariants[0].abTestWidgets[0].type;
      if (
        type === AbTestWidgetType.CROSS_SITE ||
        type === AbTestWidgetType.PRODUCT_PAGE ||
        type === AbTestWidgetType.SMART_CART
      ) {
        return type as AbTestWidgetType;
      }
    }

    // Default to CROSS_SITE
    return AbTestWidgetType.CROSS_SITE;
  }, [existingAbTest, mode, testTypeCategory]);

  const computedTestTypeCategory = AbTestWidgetTypeToTypeCategory[widgetType];
  const isThemeBasedTest = getIsThemeTest(computedTestTypeCategory);
  const isCrossSite = widgetType === AbTestWidgetType.CROSS_SITE;
  const isPdpTest = widgetType === AbTestWidgetType.PRODUCT_PAGE;
  const isSmartCart = widgetType === AbTestWidgetType.SMART_CART;

  // Check if test is running (has startedAt but no endedAt)
  const isRunning = existingAbTest?.startedAt && !existingAbTest?.endedAt;

  const TAB_ORDER = useMemo(() => {
    const baseTabs = ['Details', 'Traffic allocation'];
    if (isCrossSite) {
      // For cross-site tests, add Installation and Preview (if running)
      if (isRunning) {
        return [...baseTabs, 'Installation', 'Preview'];
      }
      return [...baseTabs, 'Installation'];
    }
    // For PDP tests, add Preview tab only if test is running
    if (isPdpTest && isRunning) {
      return [...baseTabs, 'Preview'];
    }
    return baseTabs;
  }, [isCrossSite, isPdpTest, isRunning]);

  // Fetch themes or product pages based on widget type
  const { themes, loading: themesLoading } = useThemes(isOpen && isCrossSite);
  const { productPages, loading: productPagesLoading } = useProductPages(isOpen && isPdpTest);
  const { smartCarts } = useSmartCarts(isOpen && isSmartCart);

  // Manage theme / product page / smart cart variants
  useThemeVariants({
    themes: isCrossSite ? themes : undefined,
    productPages: isPdpTest ? productPages : undefined,
    smartCarts: isSmartCart ? smartCarts : undefined,
    widgetType,
    existingAbTest,
    mode,
    isThemeBasedTest,
    setFormData,
  });

  const {
    getNextTab,
    getPreviousTab,
    handleInputChange,
    setFormDataValue,
    handleSave,
    handleTabChange,
    handleDetailsValidation,
    checkoutApi,
  } = useAbTestModalCommon({
    mode,
    formData,
    setFormData,
    rulesValid: true, // Theme tests don't have rules, so always valid
    setRulesValid: () => {}, // No-op for theme tests
    setStatisticalSignificanceError,
    statisticalSignificanceError,
    widgetType,
    shop,
    existingAbTest,
    saveTest,
    onSaveSuccess,
    onClose,
    activeTab,
    setActiveTab,
    TAB_ORDER,
  });

  useEffect(() => {
    const subscription = shop$.subscribe((shop) => {
      if (shop) {
        setShop(shop);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [shop$]);

  useEffect(() => {
    // For theme tests, form is valid when name is filled and traffic allocation is valid
    setFullFormValid(trafficAllocationValid && formData?.name?.length > 0);
  }, [formData.name, trafficAllocationValid]);

  const handleTabChangeWithValidation = useCallback(
    (tab: string) => {
      if (activeTab === 'Traffic allocation' && !trafficAllocationValid) {
        setShowInvalidStep(3);
        return;
      }
      handleTabChange(tab, setShowInvalidStep);
    },
    [activeTab, mode, trafficAllocationValid, handleTabChange, setShowInvalidStep],
  );

  const handleDetailsValidationCallback = useCallback(
    (tab: string) => handleDetailsValidation(tab, setShowInvalidStep),
    [handleDetailsValidation, setShowInvalidStep],
  );

  const renderTabContent = useCallback(
    (tabName: string) => {
      switch (tabName) {
        case 'Details':
          return (
            <AbTestModalDetailsTab
              formData={formData}
              mode={mode}
              showInvalidStep={showInvalidStep}
              statisticalSignificanceError={statisticalSignificanceError}
              setStatisticalSignificanceError={setStatisticalSignificanceError}
              setFormDataValue={setFormDataValue}
              handleInputChange={handleInputChange}
              onClose={onClose}
              getNextTab={getNextTab}
              handleTabChange={handleTabChangeWithValidation}
              handleDetailsValidation={handleDetailsValidationCallback}
              isThemeTest={true}
              hasThemePermission={hasThemePermission}
              isCrossSite={isCrossSite}
            />
          );
        case 'Traffic allocation':
          return (
            <AbTestModalTrafficAllocationTab
              widgetType={widgetType}
              mode={mode}
              showInvalidStep={showInvalidStep}
              trafficAllocationValid={trafficAllocationValid}
              fullFormValid={fullFormValid}
              getPreviousTab={getPreviousTab}
              getNextTab={getNextTab}
              isNextTabTokenForShopify={() => false}
              setActiveTab={setActiveTab}
              handleSave={handleSave}
              trafficAllocationContent={
                <ThemesAllocationSection
                  formData={formData}
                  setFormData={setFormData}
                  setTrafficAllocationValid={setTrafficAllocationValid}
                  mode={mode}
                  shop={shop}
                  runningAbTestCheckoutIds={runningAbTestCheckoutIds}
                  isLoading={themesLoading}
                  isCrossSite={isCrossSite}
                />
              }
              setShowInvalidStep={setShowInvalidStep}
              onSaveSuccess={onSaveSuccess}
            />
          );
        case 'Installation':
          return (
            <ThemeScriptTab
              mode={mode}
              getPreviousTab={getPreviousTab}
              getNextTab={getNextTab}
              setActiveTab={setActiveTab}
              onClose={onSaveSuccess}
              handleSave={handleSave}
              shop={shop}
            />
          );
        case 'Preview':
          if (isCrossSite) {
            return (
              <AbTestModalPreviewTab
                formData={formData}
                mode={mode}
                shop={shop}
                getPreviousTab={getPreviousTab}
                setActiveTab={setActiveTab}
                onClose={onClose}
              />
            );
          }
          return <PdpPreviewTab formData={formData} />;
        default:
          return null;
      }
    },
    [
      formData,
      mode,
      showInvalidStep,
      statisticalSignificanceError,
      setStatisticalSignificanceError,
      setFormDataValue,
      handleInputChange,
      onClose,
      getNextTab,
      handleTabChangeWithValidation,
      handleDetailsValidationCallback,
      widgetType,
      trafficAllocationValid,
      fullFormValid,
      getPreviousTab,
      setActiveTab,
      handleSave,
      shop,
      runningAbTestCheckoutIds,
      onSaveSuccess,
      hasThemePermission,
      isCrossSite,
      themesLoading,
    ],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="ab-test-modal">
        <AbTestModalHeader
          mode={mode}
          testTypeCategory={computedTestTypeCategory}
          onClose={onClose}
          onTestTypeChange={onTestTypeChange}
        />
        <Tabs
          activeTab={activeTab}
          setActiveTab={handleTabChangeWithValidation}
          clickableTabs={true}
          horizontalLineUnderTabs={true}
          scrollable={true}>
          {TAB_ORDER.map((tabName) => (
            <Tab key={tabName} label={tabName} showTab={true}>
              {renderTabContent(tabName)}
            </Tab>
          ))}
        </Tabs>
      </div>
    </Modal>
  );
};
